/**
 * <s-sticky-atc> — bottom bar that appears once the visitor has scrolled past a
 * share of the page and whose button scrolls back to a target section instead
 * of adding to cart.
 *
 * Attributes:
 *   data-threshold — fraction of the scrollable page (0–1) after which the bar shows. Default 0.25.
 *   data-target    — id of the element the button scrolls to.
 *
 * Markup: a [data-sticky-atc-button] button inside the element.
 * Visibility is driven by the `data-visible` attribute so styling stays in the template.
 */
class SStickyAtc extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    window.addEventListener('scroll', this.#schedule, { ...opts, passive: true });
    window.addEventListener('resize', this.#schedule, { ...opts, passive: true });
    this.addEventListener('click', this.#handleClick, opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const threshold = Number.parseFloat(this.dataset.threshold ?? '0.25');
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const visible = scrollable > 0 && window.scrollY / scrollable >= threshold;
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
  };

  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-sticky-atc-button]') : null;
    if (!button) return;
    const target = this.dataset.target ? document.getElementById(this.dataset.target) : null;
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    const focusable = target.querySelector('input:checked, input, button');
    if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
  };
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
